import React from 'react';
import ReactDOM from 'react-dom';
import { AimOutlined } from '@ant-design/icons';
import { MapModuleButton } from '../../MapModuleButton';

/**
 * @class Oskari.mapframework.bundle.mappublished.MyLocationPlugin
 *
 * Tries to locate the user by using HTML5 GeoLocation services or tries a
 * fallback to http://dev.maxmind.com/geoip/javascript GeoIP if GeoLocation is
 * not available.
 * Centers the map on the users location if location is determined successfully.
 */
Oskari.clazz.define('Oskari.mapframework.bundle.mapmodule.plugin.MyLocationPlugin',
/**
 * @static @method create called automatically on construction
 *
 *
 */
function () {
  var me = this;
  me._clazz = 'Oskari.mapframework.bundle.mapmodule.plugin.MyLocationPlugin';
  me._defaultLocation = 'top right';
  me._index = 40;
  me._name = 'MyLocationPlugin';
  this.loc = Oskari.getMsg.bind(null, 'MapModule');
  me._dialog = null;
  me._defaultIconCls = null;
  me._templates = {
    plugin: jQuery('<div class="mapplugin mylocationplugin"></div>')
  };
  this._waiting = false; // used with single location request
  this._timeouts = 0; // timeouts for single location request
  this._tracking = false;
  this._trackingOptions = null;
}, {
  /**
   * @private @method _createControlElement
   * Creates the DOM element that will be placed on the map
   * @return {jQuery}
   * Plugin jQuery element
   */
  _createControlElement: function _createControlElement() {
    return this._templates.plugin.clone();
  },
  /**
   * @private @method _setWaiting
   *
   */
  _setWaiting: function _setWaiting(bln) {
    this._waiting = !!bln;
    this._timeouts = 0;
    var el = this.getElement();
    if (!el) {
      return;
    }
    if (bln) {
      el.addClass('disabled');
    } else {
      el.removeClass('disabled');
    }
  },
  _setTracking: function _setTracking(bln) {
    this._tracking = bln;
  },
  _clearRequests: function _clearRequests() {
    this._setWaiting(false);
    this._setTracking(false);
  },
  /**
   * @public @method refresh
   */
  refresh: function refresh() {
    var _this = this;
    var el = this.getElement();
    if (!el) {
      return;
    }
    ReactDOM.render(/*#__PURE__*/React.createElement(MapModuleButton, {
      visible: this.isEnabled(),
      className: "t_mylocation",
      icon: /*#__PURE__*/React.createElement(AimOutlined, null),
      title: this.loc('plugin.MyLocationPlugin.tooltip'),
      onClick: function onClick() {
        return _this._setupRequest();
      },
      position: this.getLocation()
    }), el[0]);
  },
  /**
   * @private @method _setupRequest
   * Tries to get the geolocation from browser and move the map to the
   * location
   *
   */
  _setupRequest: function _setupRequest() {
    var opts = this._trackingOptions;
    var sb = Oskari.getSandbox();
    if (opts) {
      if (this._tracking) {
        sb.postRequestByName('StopUserLocationTrackingRequest');
        this._setTracking(false);
      } else {
        sb.postRequestByName('StartUserLocationTrackingRequest', [opts]);
        this._setTracking(true);
      }
    } else if (!this._waiting) {
      this._requestLocation();
      this._setWaiting(true);
    }
  },
  _requestLocation: function _requestLocation(timeout, highAccuracy) {
    var conf = this.getConfig();
    var options = {
      timeout: timeout || 2000,
      enableHighAccuracy: highAccuracy !== false
      // addToMap: highAccuracy !== false // or always true
      // TODO how user can clear location from map??
    };
    if (conf.zoom !== undefined) {
      options.zoomLevel = conf.zoom;
    }
    Oskari.getSandbox().postRequestByName('MyLocationPlugin.GetUserLocationRequest', [true, options]);
  },
  /**
   * Handle plugin UI and change it when desktop / mobile mode
   * @method  @public createPluginUI
   * @param  {Boolean} mapInMobileMode is map in mobile mode
   * @param {Boolean} forced application has started and ui should be rendered with assets that are available
   */
  redrawUI: function redrawUI(mapInMobileMode, forced) {
    this.refresh();
  },
  teardownUI: function teardownUI() {
    this.removeFromPluginContainer(this.getElement());
  },
  /**
   * @public @method isEnabled
   * Are the plugin's controls enabled
   * @param {Boolean} showOnlyMobile force show only mobile state
   *
   * @return {Boolean}
   * True if plugin's tools are enabled
   */
  isEnabled: function isEnabled() {
    var _ref = this.getConfig() || {},
      mobileOnly = _ref.mobileOnly;
    if (mobileOnly === true && !Oskari.util.isMobile(true)) {
      return false;
    }
    return true;
  },
  /**
   * Handle plugin start mode
   * @private @method _handleStartMode
   */
  _handleStartMode: function _handleStartMode() {
    if (!this.isEnabled()) {
      return;
    }
    var conf = this.getConfig();
    var centerMap = conf.centerMapAutomatically === true;
    if (conf.mode === 'continuous') {
      var opts = {
        addToMap: 'location'
      };
      if (centerMap) {
        opts.centerMap = 'single';
      }
      this._trackingOptions = opts;
    }
    if (centerMap) {
      // single location request on startup, use 30s timeout (browser may ask permission)
      // don't set waiting -> doesn't show errors or chain requests with different accuracy & timeouts
      this._requestLocation(30000);
    }
  },
  /**
   * @method _stopPluginImpl BasicMapModulePlugin method override
   * @param {Oskari.Sandbox} sandbox
   */
  _stopPluginImpl: function _stopPluginImpl() {
    this.teardownUI();
  },
  _startPluginImpl: function _startPluginImpl() {
    this.setElement(this._createControlElement());
    this.addToPluginContainer(this.getElement());
    this.refresh();
    this._handleStartMode();
  },
  /**
   * Checks at if device is outside of map viewport when mode is tracking.
   * If it is then move map to show device location.
   * @param {Double} lon
   * @param {Double} lat
   */
  _checkIfOutsideViewport: function _checkIfOutsideViewport(lon, lat) {
    var sandbox = this.getSandbox();
    var bbox = sandbox.getMap().getBbox();
    if (lon < bbox.left || lon > bbox.right || lat > bbox.top || lat < bbox.bottom) {
      // outside view port, center map again
      sandbox.postRequestByName('MapMoveRequest', [lon, lat]);
    }
  },
  _handleError: function _handleError(error) {
    if (this._dialog) {
      this._dialog.close();
    }
    var dialog = Oskari.clazz.create('Oskari.userinterface.component.Popup');
    var title = this.loc('plugin.MyLocationPlugin.error.title');
    var msg;
    this._dialog = dialog;
    if (error === 'denied') {
      msg = this.loc('plugin.MyLocationPlugin.error.denied');
      dialog.show(title, msg, [dialog.createCloseButton()]);
      this._clearRequests();
      return;
    }
    // Location denied only has close button, other messages fades out
    dialog.fadeout();
    msg = this.loc('plugin.MyLocationPlugin.error.noLocation');
    if (error === 'unavailable') {
      dialog.show(title, msg);
      this._clearRequests();
      return;
    }
    // timeout handling for single request
    if (this._waiting && this._timeouts < 2) {
      this._timeouts++;
      if (this._timeouts === 1) {
        msg = this.loc('plugin.MyLocationPlugin.error.timeout');
        dialog.show('', this._loc.error.timeout);
        // request high accuracy location with longer timeout
        this._requestLocation(20000);
      } else if (this._timeouts === 2) {
        // request low accuracy location
        this._requestLocation(6000, false);
      }
      return;
    }
    // show no location error and stop requests
    dialog.show(title, msg);
    this._clearRequests();
  },
  _createEventHandlers: function _createEventHandlers() {
    var _this2 = this;
    return {
      UserLocationEvent: function UserLocationEvent(event) {
        if (!_this2._tracking && !_this2._waiting) {
          return;
        }
        var error = event.getError();
        if (error) {
          _this2._handleError(error);
          return;
        }
        // success
        if (_this2._tracking) {
          _this2._checkIfOutsideViewport(event.getLon(), event.getLat());
        } else {
          _this2._setWaiting(false);
        }
      }
    };
  }
}, {
  extend: ['Oskari.mapping.mapmodule.plugin.BasicMapModulePlugin'],
  /**
   * @static @property {string[]} protocol array of superclasses
   */
  protocol: ['Oskari.mapframework.module.Module', 'Oskari.mapframework.ui.module.common.mapmodule.Plugin']
});